import { Cross2Icon } from '@radix-ui/react-icons';
import type { RowData } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ListSearch } from '@/lib/url-state';
import { DataTableSavedViews } from './data-table-saved-views';
import { DataTableSearch } from './data-table-search';
import { DataTableViewOptions } from './data-table-view-options';
import type { DataTableController } from './use-data-table';

export function DataTableToolbar<TData extends RowData, TSearch extends ListSearch>({
  controller,
  searchPlaceholder,
  filters,
  actions,
  className,
}: {
  controller: DataTableController<TData, TSearch>;
  /** Shows the quick search (`?q=`) when set. */
  searchPlaceholder?: string;
  /** The feature's filter controls, e.g. `DataTableSelectFilter`s. */
  filters?: ReactNode;
  /** Buttons such as Export, placed before the view menus. */
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {searchPlaceholder && <DataTableSearch controller={controller} placeholder={searchPlaceholder} />}
      {filters}
      {controller.activeFilters.length > 0 && (
        <Button variant="ghost" size="sm" onClick={controller.clearFilters}>
          <Cross2Icon /> Clear filters
        </Button>
      )}
      <div className="ml-auto flex items-center gap-2">
        {actions}
        <DataTableSavedViews controller={controller} />
        <DataTableViewOptions controller={controller} />
      </div>
    </div>
  );
}

/** Bulk actions for the selected rows (across pages). Hidden when nothing is selected. */
export function DataTableSelectionBar<TData extends RowData, TSearch extends ListSearch>({
  controller,
  children,
  className,
}: {
  controller: DataTableController<TData, TSearch>;
  children: (rows: TData[]) => ReactNode;
  className?: string;
}) {
  const count = controller.selectedRows.length;
  if (count === 0) return null;
  return (
    <div
      role="region"
      aria-label="Selected rows"
      className={cn('flex flex-wrap items-center gap-2 rounded-md border bg-muted/50 px-3 py-1.5 text-sm', className)}
    >
      <span className="font-medium tabular-nums">{formatNumber(count)} selected</span>
      <div className="flex flex-wrap items-center gap-2">{children(controller.selectedRows)}</div>
      <Button variant="ghost" size="sm" className="ml-auto" onClick={controller.clearSelection}>
        Clear selection
      </Button>
    </div>
  );
}
